import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Inbox,
  Search,
  Download,
  RefreshCw,
  Calendar,
  Clock,
  TrendingDown,
  Receipt,
  FileMinus,
  Truck,
  Hourglass,
  AlertTriangle
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { cn } from '../lib/cn';
import { anniDiCompetenza, type AnnoScelto } from '../lib/fatture/elencoFatture';
import { confrontaPassive, riepilogoPassive, statoPassiva, type ChiaveStato } from '../lib/fatture/elencoPassive';
import { AggiornaDaFic } from '../components/AggiornaDaFic';
import { useAuth } from '../hooks/useAuth';
import { COLORI, conta, segnoMeno, type Colore } from '../components/documenti/colori';
import { RiquadroTotale, SceltaAnno, TastoTipo } from '../components/documenti/Riepilogo';

import type { Database } from '../types/database.types';

type PassiveInvoice = Database['public']['Tables']['passive_invoices']['Row'];

/**
 * Prima questa pagina rileggeva le fatture passive dal fornitore di
 * fatturazione elettronica a ogni apertura, fino a 50 richieste HTTP di fila,
 * e non le salvava da nessuna parte. Ora legge dalla tabella, riempita ogni
 * notte dalla sincronizzazione con Fatture in Cloud.
 *
 * Le etichette dei tipi documento erano i codici SDI (TD01, TD04...): Fatture
 * in Cloud manda invece parole, quindi si mostrano come arrivano.
 *
 * Stessa grafica della pagina Fatture: anno di competenza, totali separati
 * per tipo, un colore per tipo, righe compatte.
 *
 * Ci sono anche le fatture ancora "da registrare" su Fatture in Cloud
 * (fic_pending_id pieno): si vedono con la loro bolla, ma non sono costi.
 */
const etichettaTipo: Record<string, string> = {
  expense: 'Spesa',
  passive_credit_note: 'Nota di credito',
  passive_delivery_note: 'DDT ricevuto',
};

/** Il colore della riga secondo il tipo di documento (vedi components/documenti/colori). */
const COLORE_TIPO: Record<string, Colore> = {
  passive_credit_note: 'rosso',
  passive_delivery_note: 'ambra',
};
const coloreTipo = (tipo: string | null) => COLORI[COLORE_TIPO[tipo ?? ''] ?? 'blu'];

/** Supabase non restituisce piu' di 1000 righe per lettura: si legge a blocchi. */
const BLOCCO_LETTURA = 1000;

type FiltroTipo = 'tutti' | 'passive_credit_note' | 'passive_delivery_note' | 'da_registrare';

/** La bolla di stato su ogni riga. */
const STILE_STATO: Record<ChiaveStato, string> = {
  da_registrare: 'border-violet-300 bg-violet-50 text-violet-700',
  pagata: 'border-green-200 bg-green-50 text-green-700',
  da_pagare: 'border-amber-200 bg-amber-50 text-amber-800',
  scaduta: 'border-red-200 bg-red-50 text-red-700',
  stornata: 'border-slate-200 bg-slate-100 text-slate-600',
  registrata: 'border-slate-200 bg-white text-slate-600',
};

const TITOLO_STATO: Record<ChiaveStato, string> = {
  da_registrare: 'Arrivata su Fatture in Cloud ma non ancora registrata (Acquisti → Da registrare): non conta nei costi.',
  pagata: 'Pagata, secondo Fatture in Cloud.',
  da_pagare: 'Registrata, ancora da pagare.',
  scaduta: 'Registrata, da pagare e con la scadenza passata.',
  stornata: 'Stornata su Fatture in Cloud.',
  registrata: 'Registrata su Fatture in Cloud, senza informazioni sul pagamento.',
};

/** Oggi in AAAA-MM-GG, ora locale. */
const oggiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function PassiveInvoicesPage() {
  const { addNotification } = useNotifications();
  const { profile } = useAuth();

  const [invoices, setInvoices] = useState<PassiveInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<FiltroTipo>('tutti');

  // Anno di competenza (data del documento): comanda sia la lista sia i totali.
  const annoCorrente = new Date().getFullYear();
  const [anno, setAnno] = useState<AnnoScelto>(annoCorrente);
  const [anni, setAnni] = useState<number[]>([annoCorrente]);
  // L'anno dei documenti davvero in lista: mentre si legge il nuovo anno, le
  // etichette restano su quello dei numeri mostrati.
  const [annoCaricato, setAnnoCaricato] = useState<AnnoScelto>(anno);

  // Si leggono i documenti dell'anno scelto, a blocchi; ricerca e tipo si
  // filtrano qui sotto. Se nel frattempo si cambia anno, la lettura vecchia
  // si abbandona.
  const letturaInCorso = useRef(0);
  const load = useCallback(async () => {
    const lettura = ++letturaInCorso.current;
    setLoading(true);
    setErrorMsg(null);
    const righe: PassiveInvoice[] = [];
    let errore: { message?: string } | null = null;
    for (;;) {
      let query = supabase
        .from('passive_invoices')
        .select('*')
        .order('document_date', { ascending: false, nullsFirst: false })
        .order('id')
        .range(righe.length, righe.length + BLOCCO_LETTURA - 1);
      if (anno !== 'tutti') {
        query = query.gte('document_date', `${anno}-01-01`).lt('document_date', `${anno + 1}-01-01`);
      }
      const { data, error } = await query;
      if (lettura !== letturaInCorso.current) return;
      if (error) { errore = error; break; }
      const blocco = data ?? [];
      righe.push(...blocco);
      if (blocco.length < BLOCCO_LETTURA) break;
    }

    if (errore) {
      // Meglio una lista vuota che i numeri di un altro anno sotto l'etichetta di questo.
      const msg = errore.message || 'Errore nel recupero delle fatture passive';
      setErrorMsg(msg);
      addNotification({ type: 'error', title: 'Errore', message: msg });
      setInvoices([]);
    } else {
      // Un documento arrivato fra un blocco e l'altro potrebbe comparire due volte.
      setInvoices([...new Map(righe.map((r) => [r.id, r])).values()]);
    }
    setAnnoCaricato(anno);
    setLoading(false);
  }, [anno, addNotification]);

  // Gli anni da proporre: dal primo documento all'ultimo.
  const loadAnni = useCallback(async () => {
    const base = () => supabase.from('passive_invoices').select('document_date').not('document_date', 'is', null);
    const [primo, ultimo] = await Promise.all([
      base().order('document_date', { ascending: true }).limit(1).maybeSingle(),
      base().order('document_date', { ascending: false }).limit(1).maybeSingle(),
    ]);
    if (primo.error || ultimo.error) {
      console.error('Error loading years:', primo.error ?? ultimo.error);
      return;
    }
    setAnni(anniDiCompetenza(primo.data?.document_date, ultimo.data?.document_date, annoCorrente));
  }, [annoCorrente]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnni(); }, [loadAnni]);

  const ricarica = useCallback(() => {
    load();
    loadAnni();
  }, [load, loadAnni]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return invoices
      .filter((inv) =>
        filtroTipo === 'tutti' ||
        (filtroTipo === 'da_registrare' ? inv.fic_pending_id != null : inv.document_type === filtroTipo)
      )
      .filter((inv) =>
        !q || [inv.supplier_name, inv.document_number, inv.description, inv.category].some((v) => v?.toLowerCase().includes(q))
      )
      .sort(confrontaPassive);
  }, [invoices, search, filtroTipo]);

  // I totali sommano esattamente i documenti in lista (anno, ricerca e tipo compresi).
  const riepilogo = useMemo(() => riepilogoPassive(filtered), [filtered]);

  const filtriAttivi = search.trim() !== '' || filtroTipo !== 'tutti';
  const etichettaAnno = annoCaricato === 'tutti' ? 'tutti gli anni' : String(annoCaricato);
  const cambiaTipo = (tipo: FiltroTipo) => setFiltroTipo((attuale) => (attuale === tipo ? 'tutti' : tipo));

  const oggi = oggiIso();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Passività</h1>
          <p className="text-sm text-neutral-400 mt-1">Fatture e documenti ricevuti dai fornitori, allineati da Fatture in Cloud</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AggiornaDaFic ruolo={profile?.role} cosa="passive" onAggiornato={ricarica} />
          <Button variant="outline" size="sm" onClick={ricarica} className="h-10" title="Ricarica">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Totali dell'anno di competenza, separati per tipo */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SceltaAnno id="anno-competenza-passive" anno={anno} anni={anni} onCambia={setAnno} />
          <p className="text-xs text-gray-500">
            Importi senza IVA, per data del documento{filtriAttivi && ' · con i filtri attivi'}
          </p>
        </div>

        <div className={cn('grid grid-cols-2 gap-3 lg:grid-cols-5', loading && 'opacity-60 transition-opacity')}>
          <RiquadroTotale
            scuro
            className="col-span-2 lg:col-span-1"
            etichetta={`Costi ${etichettaAnno}`}
            icona={TrendingDown}
            coloreIcona="text-rose-400"
            valore={formatCurrency(riepilogo.netto.imponibile)}
            nota={`Spese meno note di credito · ${formatCurrency(riepilogo.netto.totale)} con IVA`}
          />
          <RiquadroTotale
            etichetta="Fatture fornitori"
            icona={Receipt}
            coloreIcona="text-blue-600"
            valore={formatCurrency(riepilogo.spese.imponibile)}
            nota={`${conta(riepilogo.spese.documenti, 'documento', 'documenti')} · ${formatCurrency(riepilogo.spese.totale)} con IVA`}
          />
          <RiquadroTotale
            etichetta="Note di credito"
            icona={FileMinus}
            coloreIcona="text-red-500"
            valore={`${segnoMeno(riepilogo.noteCredito.imponibile)}${formatCurrency(riepilogo.noteCredito.imponibile)}`}
            nota={`${conta(riepilogo.noteCredito.documenti, 'nota', 'note')} · ${segnoMeno(riepilogo.noteCredito.totale)}${formatCurrency(riepilogo.noteCredito.totale)} con IVA`}
          />
          <RiquadroTotale
            etichetta="DDT ricevuti"
            icona={Truck}
            coloreIcona="text-amber-500"
            valore={String(riepilogo.ddt)}
            nota="Documenti di trasporto: non sono costi"
          />
          <RiquadroTotale
            etichetta="Da registrare"
            icona={Hourglass}
            coloreIcona="text-violet-500"
            valore={String(riepilogo.daRegistrare.documenti)}
            nota={`${formatCurrency(riepilogo.daRegistrare.totale)} con IVA · non ancora nei costi`}
            className={cn(riepilogo.daRegistrare.documenti > 0 && 'cursor-pointer hover:border-violet-400')}
            title={riepilogo.daRegistrare.documenti > 0 ? 'Mostra solo quelle da registrare' : undefined}
            onClick={() => { if (riepilogo.daRegistrare.documenti > 0) cambiaTipo('da_registrare'); }}
          />
        </div>
      </div>

      {/* Ricerca e tasti per tipo */}
      <Card className="p-1">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-full sm:basis-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <Input
              placeholder="Cerca per fornitore, numero, descrizione..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 md:h-12 border-none bg-transparent focus-visible:ring-0 text-base"
            />
          </div>
          <TastoTipo
            colore="viola"
            etichetta="Da registrare"
            acceso={filtroTipo === 'da_registrare'}
            onCambia={() => cambiaTipo('da_registrare')}
          />
          <TastoTipo
            colore="rosso"
            etichetta="Note di credito"
            acceso={filtroTipo === 'passive_credit_note'}
            onCambia={() => cambiaTipo('passive_credit_note')}
          />
          <div className="pr-1">
            <TastoTipo
              colore="ambra"
              etichetta="DDT ricevuti"
              acceso={filtroTipo === 'passive_delivery_note'}
              onCambia={() => cambiaTipo('passive_delivery_note')}
            />
          </div>
        </div>
      </Card>

      {errorMsg && (
        <Card className="p-4 border-red-200 bg-red-50">
          <div className="flex items-start gap-3 text-sm text-red-700">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Impossibile caricare le fatture passive</p>
              <p className="mt-1 text-red-600">{errorMsg}</p>
            </div>
          </div>
        </Card>
      )}

      {/* List */}
      <div className="grid grid-cols-1 gap-2">
        {loading && invoices.length === 0 ? (
          [...Array(6)].map((_, i) => <Card key={i} className="h-16 animate-pulse bg-gray-50 border-gray-100" />)
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 rounded-xl">
            <Inbox className="w-12 h-12 text-neutral-600 mx-auto mb-4 opacity-20" />
            <p className="text-neutral-400">Nessun documento trovato per {etichettaAnno}</p>
            {anno !== 'tutti' && filtriAttivi && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setAnno('tutti')}>
                Cerca in tutti gli anni
              </Button>
            )}
          </div>
        ) : (
          filtered.map((inv) => {
            const stato = statoPassiva(inv, oggi);
            const colore = coloreTipo(inv.document_type);
            const isNotaCredito = inv.document_type === 'passive_credit_note';
            const fornitore = inv.supplier_name || 'Fornitore sconosciuto';
            const dettaglio = [inv.document_number && `N. ${inv.document_number}`, inv.description, inv.category]
              .filter(Boolean)
              .join(' · ');
            return (
              <Card
                key={inv.id}
                className={cn('group py-2.5 pl-4 pr-3 sm:pl-5 sm:pr-4 hover:shadow-md transition-shadow duration-200 bg-white relative overflow-hidden', colore.bordo)}
              >
                <div className={cn('absolute inset-y-0 left-0 w-1', colore.barra)} />

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  {/* Data e tipo */}
                  <div className="w-28 shrink-0 sm:w-36">
                    <p className="flex items-center gap-1.5 font-bold text-base leading-tight text-gray-900 tabular-nums">
                      <Calendar className="w-3.5 h-3.5 text-neutral-400" />
                      {inv.document_date ? formatDate(inv.document_date) : '—'}
                    </p>
                    <span className={cn('mt-1 block text-[10px] font-semibold uppercase tracking-tight', colore.testo)}>
                      {inv.document_type ? etichettaTipo[inv.document_type] || inv.document_type : 'Documento'}
                    </span>
                  </div>

                  {/* Fornitore e descrizione */}
                  <div className="min-w-0 flex-1 basis-32 sm:basis-48">
                    <p className={cn('truncate text-[15px] font-semibold leading-tight', inv.supplier_name ? 'text-gray-900' : 'italic text-neutral-500')} title={fornitore}>
                      {fornitore}
                    </p>
                    <p className="mt-1 truncate text-xs text-gray-500" title={dettaglio || undefined}>
                      {dettaglio || '—'}
                    </p>
                  </div>

                  {/* Pagamento, scadenza, importo e allegato: a destra, o sotto se lo spazio non basta */}
                  <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                    <span
                      className={cn('inline-flex h-7 items-center rounded-full border px-3 text-xs font-semibold whitespace-nowrap', STILE_STATO[stato.chiave])}
                      title={TITOLO_STATO[stato.chiave]}
                    >
                      {stato.etichetta}
                    </span>
                    {inv.due_date && (
                      <span
                        className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 text-[11px] font-semibold text-slate-700 whitespace-nowrap"
                        title="Scadenza del pagamento"
                      >
                        <Clock className="w-3 h-3" />
                        Scade {formatDate(inv.due_date)}
                      </span>
                    )}
                    <div
                      className={cn('text-right text-base font-bold tabular-nums sm:min-w-[6.5rem]', isNotaCredito ? 'text-red-600' : 'text-gray-900')}
                      title={isNotaCredito ? 'Nota di credito: si toglie dai costi' : 'Totale del documento, IVA compresa'}
                    >
                      {isNotaCredito && '−'}{formatCurrency(inv.total_amount)}
                    </div>
                    {inv.attachment_url ? (
                      <a
                        href={inv.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-primary-600 hover:bg-slate-50"
                        title="Apri l'allegato su Fatture in Cloud"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="hidden h-8 w-8 sm:inline-block" aria-hidden />
                    )}
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
